import { extractEntity, extractList } from './tournamentsApi';
import { DEFAULT_MATCH_TZ, resolveMatchTimezone, utcKickoffToWall } from './matchKickoff';

const WEEKDAYS_LONG = {
  ru: ['', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота', 'воскресенье'],
  en: ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
};

const WEEKDAYS_SHORT = {
  ru: ['', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'],
  en: ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const pad2 = (value) => String(value).padStart(2, '0');

export const weekdayLong = (day, lang = 'ru') => {
  const table = WEEKDAYS_LONG[lang] || WEEKDAYS_LONG.ru;
  return table[day] || '';
};

export const weekdayShort = (day, lang = 'ru') => {
  const table = WEEKDAYS_SHORT[lang] || WEEKDAYS_SHORT.ru;
  return table[day] || '';
};

export const jsDayToSlotDay = (jsDay) => (jsDay === 0 ? 7 : jsDay);

export const formatClock = (hour, minute) => `${pad2(hour)}:${pad2(minute)}`;

export const DEFAULT_SCHEDULER_TIMEZONE = DEFAULT_MATCH_TZ;

/** Типовые пояса площадок AFL — стенное время места игры, не браузера админа. */
export const SCHEDULER_TIMEZONE_OPTIONS = [
  'Europe/Kaliningrad',
  'Europe/Moscow',
  'Europe/Kirov',
  'Europe/Samara',
  'Europe/Ulyanovsk',
  'Europe/Saratov',
  'Europe/Astrakhan',
  'Europe/Simferopol',
  'Europe/Minsk',
  'Europe/Kyiv',
  'Europe/Chisinau',
  'Europe/Prague',
  'Europe/Warsaw',
  'Europe/Istanbul',
  'Asia/Yerevan',
  'Asia/Yekaterinburg',
  'Asia/Omsk',
  'Asia/Novosibirsk',
  'Asia/Novokuznetsk',
  'Asia/Tomsk',
  'Asia/Barnaul',
  'Asia/Krasnoyarsk',
  'Asia/Irkutsk',
  'Asia/Chita',
  'Asia/Yakutsk',
  'Asia/Vladivostok',
  'Asia/Magadan',
  'Asia/Kamchatka',
  'Asia/Almaty',
  'Asia/Tashkent',
  'Asia/Bangkok',
];

const isStorageTimezone = (tz) => /^(utc|gmt|etc\/utc|etc\/gmt([+-]\d+)?|z)$/i.test(String(tz || '').trim());

const pickTimezoneValue = (source) => {
  if (!source) return '';
  if (typeof source === 'string') {
    const tz = String(source).trim();
    return tz && !isStorageTimezone(tz) ? tz : '';
  }
  const candidates = [
    source.venue_timezone,
    source.venueTimezone,
    source.timezone,
    source.time_zone,
    source.tz,
    source.settings?.venue_timezone,
    source.settings?.timezone,
    source.settings?.time_zone,
    source.city_timezone,
    source.city?.venue_timezone,
    source.city?.timezone,
    source.city?.time_zone,
    source.league?.venue_timezone,
    source.league?.timezone,
    source.league?.time_zone,
    source.championship?.venue_timezone,
    source.championship?.timezone,
    source.championship?.time_zone,
    source.tournament?.venue_timezone,
    source.tournament?.timezone,
    source.stadium?.venue_timezone,
    source.stadium?.timezone,
    source.stadium?.time_zone,
  ];
  for (const candidate of candidates) {
    const tz = String(candidate || '').trim();
    if (tz && !isStorageTimezone(tz)) return tz;
  }
  return '';
};

/**
 * Пояс для setAdminMatchSlots: место игры, не машина админа.
 * Порядок источников задаёт вызывающий код.
 */
export const resolveSchedulerTimezone = (...sources) => {
  for (const source of sources) {
    const tz = pickTimezoneValue(source);
    if (tz) return tz;
  }
  return DEFAULT_SCHEDULER_TIMEZONE;
};

export const parseLocalDateTime = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return null;
  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
    0,
    0,
  );
};

export const toIsoDate = (date) => {
  if (!date) return '';
  if (typeof date === 'string') {
    const match = date.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return '';
    return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(parsed.getDate())}`;
  }
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

export const formatDayMonth = (isoDate) => {
  const match = String(isoDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  return `${match[3]}.${match[2]}`;
};

export const mondayOfWeek = (isoDate) => {
  const match = String(isoDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return '';
  const offset = jsDayToSlotDay(date.getDay()) - 1;
  date.setDate(date.getDate() - offset);
  return toIsoDate(date);
};

export const dateForSlotDay = (weekDate, day) => {
  const monday = mondayOfWeek(weekDate);
  const match = String(monday || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  const slotDay = toNumber(day);
  if (!match || !slotDay || slotDay < 1 || slotDay > 7) return '';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  date.setDate(date.getDate() + (slotDay - 1));
  return toIsoDate(date);
};

export const weekRangeFromDate = (isoDate) => {
  const from = mondayOfWeek(isoDate);
  if (!from) return { from: '', to: '' };
  const match = from.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return { from, to: from };
  const sunday = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  sunday.setDate(sunday.getDate() + 6);
  return { from, to: toIsoDate(sunday) };
};

const clockParts = (time) => {
  const raw = String(time || '').slice(0, 5);
  if (!raw.includes(':')) return { hour: null, minute: null, time: '' };
  const [h, m] = raw.split(':');
  return { hour: toNumber(h), minute: toNumber(m), time: raw };
};

/** Стенные час/мин даты в IANA-поясе для момента, записанного как UTC date+time. */
export const utcStoredToWallClock = (isoDate, time, timeZone = DEFAULT_SCHEDULER_TIMEZONE) => {
  const wall = utcKickoffToWall(isoDate, time, timeZone);
  if (!wall?.date) return null;
  const parsed = new Date(`${wall.date}T12:00:00`);
  return {
    date: wall.date,
    day: Number.isNaN(parsed.getTime()) ? null : jsDayToSlotDay(parsed.getDay()),
    hour: Number(wall.time.slice(0, 2)),
    minute: Number(wall.time.slice(3, 5)),
    time: wall.time,
  };
};

const venueKey = (stadiumId, pitchId) => [
  toNumber(stadiumId) || 0,
  toNumber(pitchId) > 0 ? toNumber(pitchId) : 0,
].join('|');

const matchVenueKey = (match) => venueKey(
  match?.stadium_id ?? match?.stadium?.id,
  match?.pitch_id ?? match?.pitch?.id ?? match?.field_id,
);

/** Прикрепить уже назначенные матчи к слотам, если сервер вернул match: null. */
export const attachScheduledMatchesToSlots = (
  slots = [],
  matches = [],
  weekDate,
  timeZone = DEFAULT_SCHEDULER_TIMEZONE,
) => {
  const free = (matches || [])
    .map((item) => normalizeSchedulerMatch(item))
    .filter((item) => item?.id && matchDateValue(item) && item.time);
  if (!free.length) return slots || [];

  const used = new Set();
  return (slots || []).map((slot) => {
    if (slot?.match?.id || slot?.pendingMatch?.id) return slot;
    const slotDate = slotCalendarDate(slot, weekDate);
    const slotVenue = venueKey(slot.stadium_id, slot.pitch_id);
    const found = free.find((match) => {
      if (used.has(Number(match.id))) return false;
      if (matchVenueKey(match) !== slotVenue) return false;
      const directHour = clockParts(match.time).hour;
      const directMinute = clockParts(match.time).minute;
      const directDate = matchDateValue(match);
      const directDay = directDate
        ? jsDayToSlotDay(new Date(`${directDate}T12:00:00`).getDay())
        : null;
      const directHit = (
        directHour === Number(slot.hour)
        && directMinute === Number(slot.minute)
        && (directDate === slotDate || directDay === Number(slot.day))
      );
      if (directHit) return true;
      // Если date/time уже стенные (после normalizeAdminMatch), date_utc хранит исходный UTC.
      const utcDate = match.date_utc || null;
      const utcTime = match.time_utc || null;
      if (!utcDate || !utcTime) return false;
      const wall = utcStoredToWallClock(utcDate, utcTime, timeZone);
      if (!wall) return false;
      return (
        wall.hour === Number(slot.hour)
        && wall.minute === Number(slot.minute)
        && (wall.date === slotDate || wall.day === Number(slot.day))
      );
    });
    if (!found) return slot;
    used.add(Number(found.id));
    return { ...slot, match: found, pendingMatch: null };
  });
};

export const slotIdentity = (slot) => [
  toNumber(slot?.stadium_id) || 0,
  toNumber(slot?.pitch_id) || 0,
  toNumber(slot?.day) || 0,
  toNumber(slot?.hour) ?? 0,
  toNumber(slot?.minute) ?? 0,
].join('|');

export const matchTeamsLabel = (match) => {
  if (!match) return '';
  const home = match.home_team?.name || match.home_team_name || '';
  const guest = match.guest_team?.name || match.guest_team_name || '';
  return [home, guest].filter(Boolean).join(' - ');
};

export const matchDateValue = (match) => {
  const raw = match?.date || match?.match_date || '';
  if (!raw || match?.no_date) return '';
  return toIsoDate(raw);
};

export const formatDayMonthTime = (isoDate, time) => {
  const datePart = formatDayMonth(isoDate);
  const clock = time ? String(time).slice(0, 5) : '';
  return [datePart, clock].filter(Boolean).join(' ');
};

export const resolveWeekDate = (matches = [], slots = []) => {
  // Неделя матчей важнее isoDate слотов: по ней грузим preferences.
  const fromMatch = (matches || []).map((match) => matchDateValue(match)).find(Boolean);
  if (fromMatch) return fromMatch;
  const fromSlot = (slots || []).map((slot) => slot.isoDate).find(Boolean);
  if (fromSlot) return fromSlot;
  return toIsoDate(new Date());
};

export const normalizeSchedulerMatch = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const id = toNumber(raw.id ?? raw.match_id);
  if (!id) return null;
  const timezone = resolveMatchTimezone(raw, raw.stadium);
  const rawDate = matchDateValue(raw);
  const rawTime = raw.time ? String(raw.time).slice(0, 5) : '';
  // Уже стенные (после normalizeAdminMatch): есть date_utc.
  const alreadyWall = Boolean(raw.date_utc && raw.time_utc);
  const wall = !alreadyWall && rawDate && rawTime
    ? utcKickoffToWall(rawDate, rawTime, timezone)
    : null;
  return {
    ...raw,
    id,
    date: alreadyWall ? rawDate : (wall?.date || rawDate),
    time: alreadyWall ? rawTime : (wall?.time || rawTime),
    date_utc: raw.date_utc || rawDate,
    time_utc: raw.time_utc || rawTime,
    timezone,
    home_team_name: raw.home_team?.name || raw.home_team_name || '',
    guest_team_name: raw.guest_team?.name || raw.guest_team_name || '',
    home_fits: raw.home_fits == null ? null : toNumber(raw.home_fits),
    away_fits: raw.away_fits == null ? null : toNumber(raw.away_fits),
  };
};

let localSlotSeq = 0;

export const makeClientKey = (prefix = 'local') => {
  localSlotSeq += 1;
  return `${prefix}-${Date.now()}-${localSlotSeq}`;
};

export const normalizeSlot = (raw = {}, lang = 'ru') => {
  const hour = toNumber(raw.hour);
  const minute = toNumber(raw.minute);
  const day = toNumber(raw.day);
  const stadiumId = toNumber(raw.stadium_id ?? raw.stadium);
  const pitchId = toNumber(raw.pitch_id ?? raw.pitch ?? raw.field_id);
  const time = raw.time ? String(raw.time).slice(0, 5) : formatClock(hour || 0, minute || 0);
  return {
    ...raw,
    clientKey: raw.clientKey || (raw.id ? `srv-${raw.id}` : makeClientKey()),
    id: toNumber(raw.id),
    scheme_id: toNumber(raw.scheme_id),
    stadium_id: stadiumId,
    stadium_name: raw.stadium_name || raw.stadium?.name || '',
    pitch_id: pitchId && pitchId > 0 ? pitchId : null,
    pitch_name: raw.pitch_name || raw.pitch?.name || '',
    day,
    day_name: raw.day_name || weekdayShort(day, lang),
    hour: hour ?? 0,
    minute: minute ?? 0,
    time,
    isoDate: raw.isoDate || toIsoDate(raw.date) || '',
    match: normalizeSchedulerMatch(raw.match),
    pendingMatch: raw.pendingMatch || null,
  };
};

export const slotPayload = (slot) => {
  const body = {
    stadium_id: toNumber(slot.stadium_id),
    day: toNumber(slot.day),
    hour: toNumber(slot.hour) ?? 0,
    minute: toNumber(slot.minute) ?? 0,
  };
  const pitchId = toNumber(slot.pitch_id);
  if (pitchId && pitchId > 0) body.pitch_id = pitchId;
  return body;
};

export const wishSlotPayload = (slot) => {
  const id = toNumber(slot?.id);
  if (id && id > 0) return { id };
  return slotPayload(slot);
};

export const extractSchemes = (data) => {
  const payload = data && typeof data === 'object' && !Array.isArray(data) && data.data && !Array.isArray(data.data) && !data.id && !data.schemes
    ? data.data
    : data;
  const list = Array.isArray(payload)
    ? payload
    : (Array.isArray(payload?.schemes)
      ? payload.schemes
      : (Array.isArray(payload?.data) ? payload.data : extractList(payload)));
  return list.map((item) => {
    const scheme = extractEntity(item) || item || {};
    const rawSlots = scheme.slots || scheme.scheme_slots || scheme.scheduler_scheme_slots || [];
    return {
      ...scheme,
      id: toNumber(scheme.id),
      name: String(scheme.name || '').trim(),
      championat_id: toNumber(scheme.championat_id ?? scheme.champ_id),
      slots: extractList(rawSlots).map((slot) => normalizeSlot(slot)),
    };
  }).filter((item) => item.id);
};

export const normalizePreference = (raw = {}, matchId = null) => {
  const source = raw && typeof raw === 'object'
    ? (raw.match && typeof raw.match === 'object' ? { ...raw, ...raw.match } : raw)
    : {};
  const id = toNumber(source.match_id ?? source.id ?? matchId);
  if (!id) return null;
  const hour = toNumber(source.hour);
  const minute = toNumber(source.minute);
  const timeRaw = source.time ? String(source.time).slice(0, 5) : '';
  let resolvedHour = hour;
  let resolvedMinute = minute;
  if ((resolvedHour == null || resolvedMinute == null) && timeRaw.includes(':')) {
    const [h, m] = timeRaw.split(':');
    if (resolvedHour == null) resolvedHour = toNumber(h);
    if (resolvedMinute == null) resolvedMinute = toNumber(m);
  }
  const day = toNumber(source.day)
    || (source.date ? jsDayToSlotDay(new Date(`${toIsoDate(source.date)}T12:00:00`).getDay()) : null);
  const wallTimezone = source.wall_timezone || source.wish_timezone || null;
  return {
    id,
    match_id: id,
    home_team_id: toNumber(source.home_team_id ?? source.home_team?.id),
    guest_team_id: toNumber(source.guest_team_id ?? source.guest_team?.id ?? source.away_team_id),
    home_team_name: source.home_team?.name || source.home_team_name || '',
    guest_team_name: source.guest_team?.name || source.guest_team_name || source.away_team_name || '',
    slot_id: toNumber(source.slot_id ?? source.scheme_slot_id),
    stadium_id: toNumber(source.stadium_id ?? source.stadium),
    pitch_id: (() => {
      const pitchId = toNumber(source.pitch_id ?? source.pitch ?? source.field_id);
      return pitchId && pitchId > 0 ? pitchId : null;
    })(),
    day,
    hour: resolvedHour ?? 0,
    minute: resolvedMinute ?? 0,
    time: timeRaw || formatClock(resolvedHour || 0, resolvedMinute || 0),
    date: toIsoDate(source.date) || '',
    wall_timezone: wallTimezone,
    home_fits: source.home_fits == null ? null : toNumber(source.home_fits),
    away_fits: source.away_fits == null ? null : toNumber(source.away_fits),
    assigned: Boolean(source.assigned),
  };
};

/** wishes[] внутри матча из getAdminMatches — уже стенные в wall_timezone площадки. */
export const normalizeMatchWishes = (matchId, wishes = []) => {
  const id = toNumber(matchId);
  if (!id) return [];
  return (Array.isArray(wishes) ? wishes : [])
    .map((item) => normalizePreference(item, id))
    .filter(Boolean);
};

export const preferencesFromMatches = (matches = []) => (
  (matches || []).flatMap((match) => normalizeMatchWishes(match?.id, match?.wishes))
);

export const extractPreferences = (data) => {
  const payload = data && typeof data === 'object' && !Array.isArray(data) && data.data && !Array.isArray(data.data) && !data.schemes && !data.preferences
    ? data.data
    : data;
  const list = Array.isArray(payload?.preferences)
    ? payload.preferences
    : (Array.isArray(payload?.wishes)
      ? payload.wishes
      : (Array.isArray(payload) ? payload : []));
  return list.map(normalizePreference).filter(Boolean);
};

export const extractMatchWishes = (data) => extractPreferences(data);

export const extractSchemesPayload = (data) => ({
  schemes: extractSchemes(data),
  preferences: extractPreferences(data),
});

export const matchWishesRequestBody = (matchIds = [], date) => {
  const ids = [...new Set((matchIds || []).map((id) => Number(id)).filter(Boolean))];
  const body = { match_ids: ids };
  if (date) body.date = date;
  return body;
};

const venueTimeKey = (item) => [
  toNumber(item?.day) || 0,
  toNumber(item?.hour) ?? 0,
  toNumber(item?.minute) ?? 0,
  toNumber(item?.stadium_id) || 0,
  toNumber(item?.pitch_id) || 0,
].join('|');

const dayTimeKey = (item) => [
  toNumber(item?.day) || 0,
  toNumber(item?.hour) ?? 0,
  toNumber(item?.minute) ?? 0,
].join('|');

export const preferenceMatchesSlot = (preference, slot, { venue = true } = {}) => {
  if (!preference || !slot) return false;
  if (venue) return venueTimeKey(preference) === venueTimeKey(slot);
  return dayTimeKey(preference) === dayTimeKey(slot);
};

export const preferencesForSlot = (slot, preferences = []) => (
  (preferences || []).filter((preference) => preferenceMatchesSlot(preference, slot, { venue: true }))
);

export const preferenceForMatchSlot = (matchId, slot, preferences = [], { venue = true } = {}) => {
  const targetId = Number(matchId);
  if (!targetId || !slot) return null;
  return (preferences || []).find((preference) => (
    preferenceMatchId(preference) === targetId
    && preferenceMatchesSlot(preference, slot, { venue })
  )) || null;
};

/** Статус пожеланий капитанов для матча, стоящего в конкретном слоте. */
export const slotMatchWishStatus = (match, slot, preferences = []) => {
  if (!match?.id || !slot) return null;
  const preference = preferenceForMatchSlot(match.id, slot, preferences, { venue: true })
    || preferenceForMatchSlot(match.id, slot, preferences, { venue: false });
  if (!preference) {
    return { home: null, away: null, known: false, bothOk: false, bothBad: false };
  }
  const home = preference.home_fits == null ? null : Number(preference.home_fits);
  const away = preference.away_fits == null ? null : Number(preference.away_fits);
  return {
    home,
    away,
    known: home != null || away != null,
    bothOk: home === 1 && away === 1,
    bothBad: home === 0 && away === 0,
  };
};

export const formatPreferenceStamp = (preference, weekDate, lang = 'ru') => {
  const time = preference?.time || formatClock(preference?.hour || 0, preference?.minute || 0);
  const isoDate = preference?.date || dateForSlotDay(weekDate, preference?.day);
  const stamped = formatDayMonthTime(isoDate, time);
  if (stamped) return stamped;
  const day = weekdayShort(preference?.day, lang);
  return [day, time].filter(Boolean).join(' ');
};

const preferenceDay = (preference, weekDate) => {
  const day = toNumber(preference?.day);
  if (day) return day;
  const isoDate = preference?.date || dateForSlotDay(weekDate, preference?.day);
  if (!isoDate) return 0;
  const parsed = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return 0;
  return jsDayToSlotDay(parsed.getDay());
};

const preferenceTime = (preference) => (
  preference?.time || formatClock(preference?.hour || 0, preference?.minute || 0)
);

/** «суббота 12:00 13:00 воскресенье 12:00» */
export const formatWishTimesByDay = (preferences = [], weekDate, lang = 'ru') => {
  const byDay = new Map();
  const orphanTimes = new Set();
  (preferences || []).forEach((preference) => {
    const day = preferenceDay(preference, weekDate);
    const time = preferenceTime(preference);
    if (!time) return;
    if (!day) {
      orphanTimes.add(time);
      return;
    }
    if (!byDay.has(day)) byDay.set(day, new Set());
    byDay.get(day).add(time);
  });
  const parts = [...byDay.entries()]
    .sort((left, right) => left[0] - right[0])
    .map(([day, times]) => {
      const dayName = String(weekdayLong(day, lang) || '').toLowerCase();
      const sortedTimes = [...times].sort();
      return [dayName, ...sortedTimes].filter(Boolean).join(' ');
    })
    .filter(Boolean);
  if (orphanTimes.size) parts.push([...orphanTimes].sort().join(' '));
  return parts.join(' ');
};

export const preferenceMatchId = (item) => Number(item?.match_id ?? item?.id) || 0;

export const summarizeMatchPreferences = (matchId, preferences = [], weekDate, lang = 'ru') => {
  const targetId = Number(matchId);
  if (!targetId) return null;
  const prefs = (preferences || []).filter((item) => preferenceMatchId(item) === targetId);
  if (!prefs.length) return null;
  const homeYes = [];
  const homeNo = [];
  const awayYes = [];
  const awayNo = [];
  prefs.forEach((preference) => {
    // fits null = капитан ещё не ответил (не путать с 0 = «не подходит»).
    // Number(null) === 0 в JS — поэтому null/undefined проверяем отдельно.
    if (preference.home_fits != null) {
      if (Number(preference.home_fits) === 1) homeYes.push(preference);
      else if (Number(preference.home_fits) === 0) homeNo.push(preference);
    }
    if (preference.away_fits != null) {
      if (Number(preference.away_fits) === 1) awayYes.push(preference);
      else if (Number(preference.away_fits) === 0) awayNo.push(preference);
    }
  });
  return {
    homeYes: formatWishTimesByDay(homeYes, weekDate, lang),
    homeNo: formatWishTimesByDay(homeNo, weekDate, lang),
    awayYes: formatWishTimesByDay(awayYes, weekDate, lang),
    awayNo: formatWishTimesByDay(awayNo, weekDate, lang),
    hasAnswers: homeYes.length + homeNo.length + awayYes.length + awayNo.length > 0,
    hasPrefs: true,
  };
};

export const autoFillSlotsFromPreferences = (slots = [], preferences = [], matches = []) => {
  const next = (slots || []).map((slot) => ({ ...slot }));
  const occupied = new Set();
  const usedMatches = new Set();

  next.forEach((slot) => {
    const match = slot.pendingMatch || slot.match;
    if (!match?.id) return;
    occupied.add(slot.clientKey);
    usedMatches.add(Number(match.id));
  });

  const place = (slot, matchLike) => {
    const match = normalizeSchedulerMatch(matchLike);
    if (!slot || !match?.id || usedMatches.has(Number(match.id))) return false;
    if (occupied.has(slot.clientKey)) return false;
    slot.pendingMatch = match;
    slot.match = null;
    occupied.add(slot.clientKey);
    usedMatches.add(Number(match.id));
    return true;
  };

  const beforeCount = usedMatches.size;
  const matchById = new Map((matches || []).map((item) => [Number(item.id), item]));

  // 1) Уже проставленные на это время/площадку.
  (preferences || []).filter((preference) => preference.assigned).forEach((preference) => {
    if (usedMatches.has(Number(preference.match_id))) return;
    const slot = next.find((item) => !occupied.has(item.clientKey) && preferenceMatchesSlot(preference, item, { venue: true }));
    place(slot, matchById.get(Number(preference.match_id)) || preference);
  });

  // 2) Оба капитана удобно (fits = 1) → свободный слот только по дню/времени.
  const bothFits = (preferences || []).filter((preference) => (
    Number(preference.home_fits) === 1
    && Number(preference.away_fits) === 1
    && preference.match_id
    && !usedMatches.has(Number(preference.match_id))
  ));
  const byMatch = new Map();
  bothFits.forEach((preference) => {
    const matchId = Number(preference.match_id);
    if (!byMatch.has(matchId)) byMatch.set(matchId, []);
    byMatch.get(matchId).push(preference);
  });

  const ranked = [...byMatch.entries()].map(([matchId, prefs]) => {
    const freeKeys = new Set();
    next.forEach((slot) => {
      if (occupied.has(slot.clientKey)) return;
      if (prefs.some((preference) => preferenceMatchesSlot(preference, slot, { venue: false }))) {
        freeKeys.add(slot.clientKey);
      }
    });
    return { matchId, prefs, options: freeKeys.size };
  }).filter((item) => item.options > 0)
    .sort((left, right) => left.options - right.options || left.matchId - right.matchId);

  ranked.forEach(({ matchId, prefs }) => {
    const slot = next.find((item) => (
      !occupied.has(item.clientKey)
      && prefs.some((pref) => preferenceMatchesSlot(pref, item, { venue: false }))
    ));
    place(slot, matchById.get(matchId) || prefs[0]);
  });

  // 3) Остальные матчи из колонки «Игры» — в любые свободные слоты.
  (matches || []).filter((match) => match?.id && !usedMatches.has(Number(match.id))).forEach((match) => {
    const slot = next.find((item) => !occupied.has(item.clientKey));
    if (!slot) return;
    place(slot, match);
  });

  return {
    slots: next,
    placed: usedMatches.size - beforeCount,
  };
};

export const extractScheme = (data) => {
  const list = extractSchemes(data);
  if (list.length) return list[0];
  const entity = extractEntity(data);
  if (entity && (entity.id || entity.slots)) {
    return {
      ...entity,
      id: toNumber(entity.id),
      name: entity.name || '',
      championat_id: toNumber(entity.championat_id ?? entity.champ_id),
      slots: extractList(entity.slots).map((slot) => normalizeSlot(slot)),
    };
  }
  return null;
};

export const generateTimeSlots = ({
  startValue,
  endValue,
  durationMinutes,
  stadium,
  pitch,
  lang = 'ru',
  maxSlots = 200,
}) => {
  const start = parseLocalDateTime(startValue);
  const end = parseLocalDateTime(endValue);
  const duration = toNumber(durationMinutes);
  if (!start || !end || !duration || duration <= 0) {
    return { slots: [], error: 'tsNeedRange' };
  }
  if (end.getTime() <= start.getTime()) {
    return { slots: [], error: 'tsRangeInvalid' };
  }
  if (!stadium?.id) {
    return { slots: [], error: 'tsNeedStadium' };
  }

  const step = duration * 60 * 1000;
  const slots = [];
  let cursor = start.getTime();
  while (cursor + step <= end.getTime()) {
    if (slots.length >= maxSlots) {
      return { slots, error: 'tsTooManySlots' };
    }
    const date = new Date(cursor);
    const day = jsDayToSlotDay(date.getDay());
    slots.push(normalizeSlot({
      clientKey: makeClientKey('gen'),
      stadium_id: stadium.id,
      stadium_name: stadium.name || '',
      pitch_id: pitch?.id || null,
      pitch_name: pitch?.name || '',
      day,
      day_name: weekdayShort(day, lang),
      hour: date.getHours(),
      minute: date.getMinutes(),
      isoDate: toIsoDate(date),
    }, lang));
    cursor += step;
  }
  if (!slots.length) return { slots: [], error: 'tsNoSlotsFit' };
  return { slots, error: '' };
};

export const mergeSlots = (existing = [], incoming = []) => {
  const seen = new Set(existing.map(slotIdentity));
  const next = existing.slice();
  incoming.forEach((slot) => {
    const key = slotIdentity(slot);
    if (seen.has(key)) return;
    seen.add(key);
    next.push(slot);
  });
  return next;
};

export const slotCalendarDate = (slot, weekDate) => (
  slot?.isoDate || dateForSlotDay(weekDate, slot?.day) || ''
);

export const groupSlotsForDisplay = (slots = [], weekDate, lang = 'ru') => {
  const groups = [];
  const dateIndex = new Map();

  [...slots].sort((a, b) => {
    const dateA = slotCalendarDate(a, weekDate);
    const dateB = slotCalendarDate(b, weekDate);
    if (dateA !== dateB) return String(dateA).localeCompare(String(dateB)) || ((a.day || 0) - (b.day || 0));
    const stadiumA = a.stadium_name || '';
    const stadiumB = b.stadium_name || '';
    if (stadiumA !== stadiumB) return stadiumA.localeCompare(stadiumB, lang);
    const pitchA = a.pitch_name || '';
    const pitchB = b.pitch_name || '';
    if (pitchA !== pitchB) return pitchA.localeCompare(pitchB, lang);
    return ((a.hour || 0) * 60 + (a.minute || 0)) - ((b.hour || 0) * 60 + (b.minute || 0));
  }).forEach((slot) => {
    const isoDate = slotCalendarDate(slot, weekDate);
    const dateKey = isoDate || `day-${slot.day || 0}`;
    if (!dateIndex.has(dateKey)) {
      const day = slot.day || (isoDate ? jsDayToSlotDay(new Date(`${isoDate}T12:00:00`).getDay()) : 0);
      dateIndex.set(dateKey, {
        dateKey,
        isoDate,
        day,
        dateLabel: isoDate
          ? `${formatDayMonth(isoDate)} ${weekdayShort(day, lang)}`
          : weekdayShort(day, lang),
        stadiums: [],
        stadiumIndex: new Map(),
      });
      groups.push(dateIndex.get(dateKey));
    }
    const dateGroup = dateIndex.get(dateKey);
    const stadiumKey = String(slot.stadium_id || slot.stadium_name || '');
    if (!dateGroup.stadiumIndex.has(stadiumKey)) {
      const stadiumGroup = {
        stadium_id: slot.stadium_id,
        stadium_name: slot.stadium_name || '',
        pitches: [],
        pitchIndex: new Map(),
      };
      dateGroup.stadiumIndex.set(stadiumKey, stadiumGroup);
      dateGroup.stadiums.push(stadiumGroup);
    }
    const stadiumGroup = dateGroup.stadiumIndex.get(stadiumKey);
    const pitchKey = String(slot.pitch_id || slot.pitch_name || 'none');
    if (!stadiumGroup.pitchIndex.has(pitchKey)) {
      const pitchGroup = {
        pitch_id: slot.pitch_id,
        pitch_name: slot.pitch_name || '',
        slots: [],
      };
      stadiumGroup.pitchIndex.set(pitchKey, pitchGroup);
      stadiumGroup.pitches.push(pitchGroup);
    }
    stadiumGroup.pitchIndex.get(pitchKey).slots.push(slot);
  });

  return groups.map(({ stadiumIndex, ...group }) => ({
    ...group,
    stadiums: group.stadiums.map(({ pitchIndex, ...stadium }) => stadium),
  }));
};

export const formatSlotsTxt = (slots = [], weekDate, lang = 'ru') => {
  const filled = (slots || []).filter((slot) => slot.match || slot.pendingMatch);
  const groups = groupSlotsForDisplay(filled, weekDate, lang);
  const lines = [];
  groups.forEach((dateGroup) => {
    if (lines.length) lines.push('');
    lines.push(dateGroup.dateLabel);
    dateGroup.stadiums.forEach((stadium) => {
      lines.push(stadium.stadium_name || '');
      stadium.pitches.forEach((pitch) => {
        if (pitch.pitch_name) lines.push(pitch.pitch_name);
        pitch.slots.forEach((slot) => {
          const match = slot.pendingMatch || slot.match;
          const time = slot.time || formatClock(slot.hour, slot.minute);
          lines.push(`${time} ${matchTeamsLabel(match)}`.trim());
        });
      });
    });
  });
  return lines.join('\n').trim();
};

export const downloadTextFile = (text, filename) => {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const mergeSchemeMatches = (localSlots, schemeSlots = []) => {
  const byId = new Map();
  const byIdentity = new Map();
  schemeSlots.forEach((slot) => {
    const normalized = normalizeSlot(slot);
    if (normalized.id) byId.set(normalized.id, normalized);
    byIdentity.set(slotIdentity(normalized), normalized);
  });
  return localSlots.map((slot) => {
    const incoming = (slot.id && byId.get(slot.id)) || byIdentity.get(slotIdentity(slot));
    if (!incoming) return slot;
    return {
      ...slot,
      id: slot.id || incoming.id,
      match: incoming.match,
      stadium_name: slot.stadium_name || incoming.stadium_name,
      pitch_name: slot.pitch_name || incoming.pitch_name,
    };
  });
};
